import { useState } from "react";
import { useNavigate } from "react-router";
import { describeError } from "@/api/client";
import type { Uuid } from "@/api/types";
import { useCanEdit } from "@/api/session";
import { Badge, Button, Card, Checkbox, IconButton, InlineAlert, NumberField, useToast } from "@/components";
import { Decimal, fractionToPercent, percentToFraction } from "@/lib/decimal";
import { formatFractionAsPercent, formatNumber, pluralize } from "@/lib/format";
import {
  canMake,
  usePurchasingSettings,
  useShoppingList,
  type CanMakeResult,
  type FormulaVersionOption,
  type MaterialOption,
} from "../api";
import { CoverageTable } from "./CoverageTable";
import { FormulaVersionCombobox, MaterialCombobox } from "./Pickers";
import styles from "./ShoppingListBuilder.module.css";

interface Row {
  key: number;
  version: FormulaVersionOption | null;
  batch: string | null;
  concentrationPct: string | null;
}

interface RowErrors {
  version?: string;
  batch?: string;
  concentration?: string;
}

let nextKey = 1;
function newRow(): Row {
  nextKey += 1;
  return { key: nextKey, version: null, batch: "50", concentrationPct: "100" };
}

function validate(row: Row): RowErrors {
  const errors: RowErrors = {};
  if (!row.version) errors.version = "Choose a formula version.";
  const batch = Decimal.tryFrom(row.batch ?? "");
  if (!batch || batch.compare(0) <= 0) errors.batch = "Batch size must be more than 0 g.";
  const pct = Decimal.tryFrom(row.concentrationPct ?? "");
  if (!pct || pct.compare(0) <= 0 || pct.compare(100) > 0)
    errors.concentration = "Use a concentration above 0 and up to 100 %.";
  return errors;
}

/**
 * "Can I make this?": formula versions at batch sizes checked against neat stock, then the shortfall
 * (plus an optional safety margin) becomes a basket.
 */
export function ShoppingListBuilder() {
  const [rows, setRows] = useState<Row[]>(() => [newRow()]);
  const [errors, setErrors] = useState<Record<number, RowErrors>>({});
  const [subtractStock, setSubtractStock] = useState(true);
  const [marginPct, setMarginPct] = useState<string | null>(null);
  const [solvent, setSolvent] = useState<MaterialOption | null>(null);
  const [results, setResults] = useState<CanMakeResult[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<unknown>(null);
  const settings = usePurchasingSettings();
  const shoppingList = useShoppingList();
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const toast = useToast();
  const margin = marginPct ?? (settings.data ? fractionToPercent(settings.data.default_safety_margin) : "0");

  function update(key: number, changes: Partial<Row>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));
    setResults(null);
  }

  function validRows(): Row[] | null {
    const found: Record<number, RowErrors> = {};
    for (const row of rows) {
      const rowErrors = validate(row);
      if (Object.keys(rowErrors).length) found[row.key] = rowErrors;
    }
    setErrors(found);
    return Object.keys(found).length ? null : rows;
  }

  function items(valid: Row[]) {
    return valid.map((row) => ({
      formula_version_id: row.version?.id as Uuid,
      batch_g: row.batch ?? "0",
      concentration: percentToFraction(row.concentrationPct ?? "100"),
    }));
  }

  async function checkStock() {
    const valid = validRows();
    if (!valid) return;
    setChecking(true);
    setCheckError(null);
    try {
      const checked = await Promise.all(
        items(valid).map((item) => canMake({ ...item, solvent_material_id: solvent?.id ?? null })),
      );
      setResults(checked);
    } catch (error) {
      setCheckError(error);
      setResults(null);
    } finally {
      setChecking(false);
    }
  }

  function createBasket() {
    const valid = validRows();
    if (!valid) return;
    shoppingList.mutate(
      {
        items: items(valid),
        safety_margin: percentToFraction(margin || "0"),
        subtract_stock: subtractStock,
        solvent_material_id: solvent?.id ?? null,
      },
      {
        onSuccess: (result) => {
          if (!result.basket) return;
          const lines = result.basket.line_count;
          if (lines === 0) {
            toast.info("Everything is on the shelf", { description: "Basket created with no lines; nothing to buy." });
          } else {
            toast.success(`Basket created with ${pluralize(lines, "line", "lines")}`);
          }
          void navigate(`/purchasing/baskets/${result.basket.id}`);
        },
      },
    );
  }

  const short = results?.reduce((sum, result) => sum + result.short_count, 0) ?? 0;

  return (
    <Card
      title="Can I make this?"
      description="Check a formula against the neat grams on your shelf (dilution lots count), then turn the shortfall into a basket."
    >
      <div className={styles.builder}>
        <ol className={styles.rows}>
          {rows.map((row, index) => (
            <li key={row.key} className={styles.row}>
              <FormulaVersionCombobox
                label="Formula version"
                hideLabel={index > 0}
                value={row.version}
                onValueChange={(version) => update(row.key, { version })}
                error={errors[row.key]?.version}
                className={styles.version}
              />
              <NumberField
                label="Batch size"
                hideLabel={index > 0}
                unit="g"
                min={0}
                step={0.001}
                value={row.batch}
                onValueChange={(batch) => update(row.key, { batch })}
                error={errors[row.key]?.batch}
                className={styles.number}
              />
              <NumberField
                label="Concentration"
                hideLabel={index > 0}
                unit="%"
                min={0}
                max={100}
                step={0.01}
                value={row.concentrationPct}
                onValueChange={(concentrationPct) => update(row.key, { concentrationPct })}
                error={errors[row.key]?.concentration}
                className={styles.number}
              />
              <IconButton
                icon="trash"
                label="Remove this formula"
                className={styles.remove}
                disabled={rows.length === 1}
                onClick={() => {
                  setRows((current) => current.filter((candidate) => candidate.key !== row.key));
                  setResults(null);
                }}
              />
            </li>
          ))}
        </ol>
        <div>
          <Button variant="ghost" icon="plus" onClick={() => setRows((current) => [...current, newRow()])}>
            Add another formula
          </Button>
        </div>
        <div className={styles.options}>
          <Checkbox
            label="Subtract what’s on the shelf"
            description="Turn off to buy the full amounts."
            checked={subtractStock}
            onCheckedChange={setSubtractStock}
          />
          <NumberField
            label="Safety margin"
            description="Extra on top of what the formulas need."
            unit="%"
            min={0}
            max={100}
            step={1}
            value={margin}
            onValueChange={setMarginPct}
            className={styles.number}
          />
          <MaterialCombobox
            label="Solvent for diluting"
            optional
            description="Checks the ethanol for batches below 100 %."
            value={solvent}
            onValueChange={(value) => {
              setSolvent(value);
              setResults(null);
            }}
            className={styles.solvent}
          />
        </div>
        {checkError ? (
          <InlineAlert tone="danger" title="Couldn’t check the stock">
            {describeError(checkError)}
          </InlineAlert>
        ) : null}
        {shoppingList.isError ? (
          <InlineAlert tone="danger" title="Couldn’t create the basket">
            {describeError(shoppingList.error)}
          </InlineAlert>
        ) : null}
        <div className={styles.actions}>
          <Button onClick={() => void checkStock()} pending={checking} pendingLabel="Checking…">
            Check stock
          </Button>
          <Button
            variant="primary"
            icon="cart"
            onClick={createBasket}
            pending={shoppingList.isPending}
            pendingLabel="Creating…"
            disabled={!canEdit}
          >
            Create basket from shortfall
          </Button>
          {results ? (
            short ? (
              <Badge tone="warning" icon="alertTriangle">
                {pluralize(short, "material short", "materials short")}
              </Badge>
            ) : (
              <Badge tone="success" icon="check">
                You can make {results.length === 1 ? "it" : "them all"}
              </Badge>
            )
          ) : null}
        </div>
        {results?.map((result, index) => {
          const percent = formatFractionAsPercent(result.concentration, { minDecimals: 0, maxDecimals: 2 });
          const heading = `${result.formula_version.code} ${result.formula_version.label} at ${formatNumber(result.batch_g)} g, ${percent}`;
          return (
            <section key={`${result.formula_version.id}-${index}`} className={styles.result} aria-label={heading}>
              <h3 className={styles.resultTitle}>
                {heading}
                <span className={styles.resultMeta}>
                  {result.can_make
                    ? "everything on the shelf"
                    : `${pluralize(result.short_count, "line", "lines")} short`}
                </span>
              </h3>
              <CoverageTable
                caption={`Stock for ${heading}`}
                lines={result.solvent ? [...result.lines, result.solvent] : result.lines}
              />
              {result.warnings.length ? (
                <ul className={styles.warnings}>
                  {result.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          );
        })}
      </div>
    </Card>
  );
}
